import { Controller } from '@hotwired/stimulus'

/**
 * Stimulus-контроллер карты локации клуба на Google Maps.
 *
 * Ожидает готовность Google Maps API, строит карту по координатам клуба,
 * добавляет кастомный маркер и пересоздает карту при смене темы сайта, чтобы
 * применить светлую или темную цветовую схему.
 *
 * @extends {Controller}
 */
export default class ClubLocationMapController extends Controller {
  /**
   * DOM-цели контроллера.
   *
   * `canvas` — элемент, в котором Google Maps отрисовывает карту.
   *
   * @type {string[]}
   */
  static targets = ['canvas']

  /**
   * Значения, передаваемые из разметки через Stimulus data-атрибуты.
   *
   * `latitude` и `longitude` задают координаты клуба, `mapId` — Google Maps
   * Map ID, `title` — подпись маркера, `zoom` — начальный масштаб карты,
   * `pinImage` — опциональное изображение кастомного маркера.
   *
   * @type {{
   *   latitude: NumberConstructor,
   *   longitude: NumberConstructor,
   *   mapId: StringConstructor,
   *   title: StringConstructor,
   *   zoom: { type: NumberConstructor, default: number },
   *   pinImage: StringConstructor
   * }}
   */
  static values = {
    latitude: Number,
    longitude: Number,
    mapId: String,
    title: String,
    zoom: { type: Number, default: 15 },
    pinImage: String
  }

  /**
   * Текущий инстанс карты Google Maps.
   *
   * @type {google.maps.Map | null}
   */
  #map = null

  /**
   * Текущий кастомный маркер клуба.
   *
   * @type {google.maps.marker.AdvancedMarkerElement | null}
   */
  #marker = null

  /**
   * Обработчик события готовности Google Maps API.
   *
   * @type {(() => void) | null}
   */
  #onMapsReady = null

  /**
   * Обработчик смены темы сайта.
   *
   * @type {((event: CustomEvent) => void) | null}
   */
  #onThemeChange = null

  /**
   * Тема, с которой сейчас отрисована карта.
   *
   * @type {'light' | 'dark' | null}
   */
  #theme = null

  /**
   * Подписывается на готовность Google Maps API и смену темы.
   *
   * Если API уже загружен, сразу запускает отрисовку карты.
   */
  connect() {
    this.#onMapsReady = this.#render.bind(this)
    this.#onThemeChange = this.#handleThemeChange.bind(this)
    document.addEventListener('google-maps:maps-ready', this.#onMapsReady)
    document.addEventListener('google-maps:ready', this.#onMapsReady)
    document.addEventListener('theme:change', this.#onThemeChange)

    if (window.google?.maps?.importLibrary) this.#render()
  }

  /**
   * Снимает обработчики событий и отсоединяет маркер от карты.
   */
  disconnect() {
    document.removeEventListener('google-maps:maps-ready', this.#onMapsReady)
    document.removeEventListener('google-maps:ready', this.#onMapsReady)
    document.removeEventListener('theme:change', this.#onThemeChange)
    if (this.#marker) this.#marker.map = null
    this.#map = null
    this.#marker = null
  }

  /**
   * Создает карту или пересоздает ее с актуальной цветовой схемой.
   *
   * По умолчанию повторная отрисовка пропускается, если карта уже создана.
   * При `preserveViewport` сохраняет текущие центр и масштаб, что позволяет
   * менять тему без сброса позиции пользователя.
   *
   * @param {Object} [options={}] — параметры отрисовки
   * @param {boolean} [options.preserveViewport=false] — сохранить центр и zoom
   * @param {boolean} [options.force=false] — принудительно пересоздать карту
   */
  async #render({ preserveViewport = false, force = false } = {}) {
    if ((this.#map && !force) || !this.hasCanvasTarget) return

    let Map

    try {
      ;({ Map } = await google.maps.importLibrary('maps'))
    } catch (error) {
      console.error('Failed to load Google Maps library', error)
      return
    }

    const position = {
      lat: this.latitudeValue,
      lng: this.longitudeValue
    }

    const hasValidPosition =
      Number.isFinite(position.lat) &&
      Number.isFinite(position.lng) &&
      position.lat >= -90 &&
      position.lat <= 90 &&
      position.lng >= -180 &&
      position.lng <= 180

    if (!hasValidPosition) return

    const theme = this.#currentTheme()
    const center = preserveViewport
      ? this.#map?.getCenter()?.toJSON()
      : position
    const zoom = preserveViewport ? this.#map?.getZoom() : this.zoomValue

    if (this.#marker) this.#marker.map = null

    this.#map = new Map(this.canvasTarget, {
      backgroundColor: theme === 'dark' ? '#202126' : '#ffffff',
      center: center || position,
      colorScheme: theme === 'dark' ? 'DARK' : 'LIGHT',
      fullscreenControl: true,
      gestureHandling: 'cooperative',
      mapTypeControl: false,
      scrollwheel: false,
      streetViewControl: false,
      cameraControl: false,
      zoom: Number.isFinite(zoom) ? zoom : this.zoomValue,
      zoomControl: true,
      mapId: this.mapIdValue
    })
    this.#theme = theme

    this.#addMarker(this.#map)
  }

  /**
   * Загружает библиотеку маркеров Google Maps и добавляет маркер клуба.
   *
   * Если за время асинхронной загрузки карта была пересоздана, устаревший
   * маркер не добавляется.
   *
   * @param {google.maps.Map} map — карта, к которой нужно привязать маркер
   * @returns {Promise<void>}
   */
  async #addMarker(map) {
    if (!this.hasMapIdValue || this.mapIdValue.trim() === '') {
      console.warn('mapIdValue is required to add a marker.')
      return
    }

    let AdvancedMarkerElement

    try {
      ;({ AdvancedMarkerElement } = await google.maps.importLibrary('marker'))
    } catch (error) {
      console.error('Failed to load Google Maps marker library', error)
      return
    }

    if (map !== this.#map) return

    const marker = new AdvancedMarkerElement({
      map,
      position: {
        lat: this.latitudeValue,
        lng: this.longitudeValue
      },
      title: this.titleValue,
      anchorLeft: '-50%',
      anchorTop: '-50%'
    })

    if (this.hasPinImageValue) {
      const pinImage = document.createElement('img')
      pinImage.src = this.pinImageValue
      pinImage.width = 56
      pinImage.height = 56

      marker.append(pinImage)
    }

    marker.classList.add('logo-marker')
    this.#marker = marker
  }

  /**
   * Обрабатывает событие смены темы сайта и пересоздает карту при необходимости.
   *
   * @param {CustomEvent<{ theme?: string }>} event — событие `theme:change`
   */
  #handleThemeChange(event) {
    const theme = this.#normalizeTheme(event.detail?.theme)

    if (!theme || theme === this.#theme) return
    if (!window.google?.maps || !this.#map) {
      this.#theme = theme
      return
    }

    this.#render({ preserveViewport: true, force: true })
  }

  /**
   * Возвращает текущую тему страницы для цветовой схемы карты.
   *
   * @returns {'light' | 'dark'}
   */
  #currentTheme() {
    return (
      this.#normalizeTheme(document.documentElement.dataset.theme) || 'light'
    )
  }

  /**
   * Приводит произвольное значение темы к поддерживаемому набору.
   *
   * @param {string | null | undefined} theme — проверяемое значение темы
   * @returns {'light' | 'dark' | null}
   */
  #normalizeTheme(theme) {
    return ['light', 'dark'].includes(theme) ? theme : null
  }
}
